import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { UserRound } from 'lucide-react';

import { fetchConfiguracaoPorId, trocarAdvogado, type ConversaDetalhe } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { nomeDoParticipante } from '../advogadoEmEdicao';

const ESTADOS_ENCERRADOS = new Set(['CONCLUSAO', 'ESCALONAMENTO_HUMANO', 'FORA_DE_ESCOPO']);

/** O backend pede a confirmação quando o contrato ou a procuração já saíram em nome do anterior. */
function pedeConfirmacao(error: unknown): string | null {
  if (isAxiosError(error) && error.response?.status === 409 && error.response.data?.codigo === 'instrumentos_gerados') {
    return String(error.response.data.detail ?? '');
  }
  return null;
}

/**
 * Quem cuida do atendimento e, para quem gere o Secretário do escritório, a troca
 * (docs/PLANO_SECRETARIO_ESCRITORIO.md §6). Só o atendimento encerrado troca de
 * advogado — o backend diz por quê quando não dá.
 */
export function QuemCuida({ dossie }: { dossie: ConversaDetalhe }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [aberto, setAberto] = useState(false);
  const [escolhido, setEscolhido] = useState('');
  const [confirmacao, setConfirmacao] = useState<string | null>(null);
  const configuracaoId = dossie.configuracao_id ?? '';

  const { data: config, isLoading } = useQuery({
    queryKey: ['secretario', configuracaoId, 'config'],
    queryFn: () => fetchConfiguracaoPorId(configuracaoId),
    enabled: aberto && !!configuracaoId,
  });
  const atual = dossie.conversa.advogado_id ?? null;
  const opcoes = (config?.participantes ?? []).filter((p) => p.ativo && p.pronto && p.usuario_id !== atual);

  const trocar = useMutation({
    mutationFn: (confirmar: boolean) => trocarAdvogado(dossie.conversa.id, escolhido, confirmar),
    onSuccess: (atualizado) => {
      queryClient.setQueryData(queryKeys.secretario.atendimento(dossie.conversa.id), atualizado);
      queryClient.invalidateQueries({ queryKey: ['secretario'] });
      setAberto(false);
      setConfirmacao(null);
      setEscolhido('');
      showSuccess('Atendimento passado ao advogado.');
    },
    onError: (error) => {
      const motivo = pedeConfirmacao(error);
      if (motivo) {
        setConfirmacao(motivo);
        return;
      }
      notifyMutationError(error, showError);
    },
  });

  if (!dossie.pode_trocar_advogado) return null;
  // No meio da conversa a troca não vale (as respostas são das perguntas do advogado
  // de antes): o botão só aparece no atendimento encerrado.
  const encerrado = ESTADOS_ENCERRADOS.has(dossie.conversa.estado);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <UserRound className="h-4 w-4" />
          Quem cuida
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-2">
        <span className={atual ? 'text-sm' : 'text-sm font-medium text-amber-700 dark:text-amber-300'}>
          {atual ? dossie.conversa.advogado_nome : 'Sem advogado'}
        </span>
        {encerrado ? (
          <Button type="button" variant="outline" className="min-h-11" onClick={() => setAberto(true)}>
            {atual ? 'Trocar advogado' : 'Escolher advogado'}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">Dá para trocar quando o atendimento terminar.</span>
        )}
      </CardContent>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Passar o atendimento a outro advogado</DialogTitle>
            <DialogDescription>Ele recebe um aviso e passa a ver o atendimento.</DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <Spinner size={32} label="Carregando os advogados" />
          ) : opcoes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum outro advogado do Secretário está pronto para atender.</p>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="novo-advogado">Advogado</Label>
              <Select value={escolhido} onValueChange={setEscolhido}>
                <SelectTrigger id="novo-advogado" className="h-11">
                  <SelectValue placeholder="Escolha o advogado" />
                </SelectTrigger>
                <SelectContent>
                  {opcoes.map((p) => (
                    <SelectItem key={p.usuario_id} value={p.usuario_id}>
                      {nomeDoParticipante(p)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button type="button" disabled={!escolhido || trocar.isPending} onClick={() => trocar.mutate(false)}>
              {trocar.isPending && <Spinner className="mr-2 text-inherit" />}
              Passar o atendimento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmacao !== null}
        title="O contrato já saiu em nome do outro advogado"
        message={confirmacao ?? ''}
        confirmLabel="Passar mesmo assim"
        confirmColor="warning"
        loading={trocar.isPending}
        onConfirm={() => trocar.mutate(true)}
        onCancel={() => setConfirmacao(null)}
      />
    </Card>
  );
}
